import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles, Lock, ArrowRight, TrendingUp, Target, Lightbulb, Zap, CheckCircle, MessageCircle, Users } from 'lucide-react'
import Modal from './Modal'

export interface DemoInsight {
  category: string
  icon: typeof Sparkles
  color: string
  bgColor: string
  title: string
  content: string
  actionable: string
}

// Default search-focused insights
export const SEARCH_DEMO_INSIGHTS: DemoInsight[] = [
  {
    category: 'Overall Assessment',
    icon: TrendingUp,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    title: 'Your AI Visibility Score is Growing',
    content: 'Your brand mentions increased 23% this month across ChatGPT and Claude. You\'re now appearing in 67% of relevant search queries, up from 52% last month.',
    actionable: 'Continue publishing content that answers common customer questions to maintain this momentum.'
  },
  {
    category: 'Top Opportunity',
    icon: Target,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    title: 'Competitors Missing in "Best Local" Queries',
    content: 'Your competitors are barely visible in "best [service] near me" queries. This is a prime opportunity to dominate local AI search results.',
    actionable: 'Add location-specific content and update your Google Business Profile to improve local AI visibility.'
  },
  {
    category: 'Quick Win',
    icon: Zap,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    title: 'Add Customer Reviews to Your Site',
    content: 'AI assistants frequently cite customer testimonials. Your site has limited social proof compared to competitors who are mentioned more often.',
    actionable: 'Add a testimonials page or embed Google reviews on your homepage within this week.'
  },
  {
    category: 'Competitive Insight',
    icon: Lightbulb,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    title: 'Competitor X is Winning on Comparison Queries',
    content: 'When users ask AI "which is better: [you] or [competitor]", the competitor is favored 70% of the time due to more detailed pricing and feature information.',
    actionable: 'Create a comparison page or FAQ addressing common competitive questions.'
  }
]

// Market-focused insights
export const MARKET_DEMO_INSIGHTS: DemoInsight[] = [
  {
    category: 'Market Position',
    icon: TrendingUp,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    title: 'Strong Position in Premium Segment',
    content: 'AI assistants consistently position your brand in the premium tier when discussing industry options. Your pricing and quality messaging is resonating with AI recommendation engines.',
    actionable: 'Double down on premium positioning by highlighting unique features and quality differentiators in your content.'
  },
  {
    category: 'Value Proposition',
    icon: Users,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    title: 'Customer Service is Your Key Differentiator',
    content: 'When AI assistants compare you to competitors, your customer service and support are mentioned 3x more often than any other attribute.',
    actionable: 'Feature customer service testimonials and support guarantees more prominently on your website.'
  },
  {
    category: 'Channel Opportunity',
    icon: MessageCircle,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    title: 'Untapped LinkedIn Authority',
    content: 'Your competitors are being cited from their LinkedIn thought leadership content. You have minimal LinkedIn presence in AI training data.',
    actionable: 'Start publishing weekly LinkedIn articles on industry topics to build authority signals.'
  },
  {
    category: 'Competitive Gap',
    icon: Target,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    title: 'Pricing Transparency Needed',
    content: 'Competitors with clear pricing pages are recommended 40% more often for "budget-conscious" queries. Your pricing requires a quote request.',
    actionable: 'Add a pricing page with at least starting prices or pricing tiers to capture budget-conscious AI recommendations.'
  }
]

interface InsightsUpgradeCardProps {
  title?: string
  description?: string
  demoInsights?: DemoInsight[]
}

export default function InsightsUpgradeCard({
  title = 'AI Insights & Recommendations',
  description = 'Unlock personalized AI marketing strategies and competitive insights',
  demoInsights = SEARCH_DEMO_INSIGHTS
}: InsightsUpgradeCardProps) {
  const navigate = useNavigate()
  const [showModal, setShowModal] = useState(false)
  const [expandedInsight, setExpandedInsight] = useState<number | null>(null)

  const insights = demoInsights

  return (
    <>
      {/* Subtle Upgrade Card */}
      <div
        onClick={() => setShowModal(true)}
        className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/30 transition-all group"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gray-200 group-hover:bg-indigo-100 rounded-lg transition-colors">
              <Sparkles className="h-6 w-6 text-gray-500 group-hover:text-indigo-600 transition-colors" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-700 group-hover:text-gray-900">{title}</h3>
                <Lock className="h-4 w-4 text-gray-400" />
              </div>
              <p className="text-sm text-gray-500 mt-0.5">
                {description}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 text-gray-400 group-hover:text-indigo-600 transition-colors">
            <span className="hidden sm:inline text-sm font-medium">Preview</span>
            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>

      {/* Demo Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} size="3xl">
        {/* Custom gradient header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between flex-shrink-0 rounded-t-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">AI Insights & Recommendations</h2>
              <p className="text-sm text-white/80">Example insights you'll receive with Pro plan</p>
            </div>
          </div>
        </div>
        <Modal.Body>
              <div className="space-y-4">
                {insights.map((insight, index) => {
                  const Icon = insight.icon
                  const isExpanded = expandedInsight === index

                  return (
                    <div
                      key={index}
                      className={`border rounded-lg overflow-hidden transition-all ${
                        isExpanded ? 'border-indigo-300 shadow-md' : 'border-gray-200 hover:border-indigo-200'
                      }`}
                    >
                      <button
                        onClick={() => setExpandedInsight(isExpanded ? null : index)}
                        className="w-full p-4 flex items-start gap-4 text-left"
                      >
                        <div className={`p-2 rounded-lg ${insight.bgColor} flex-shrink-0`}>
                          <Icon className={`h-5 w-5 ${insight.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-semibold uppercase tracking-wide ${insight.color}`}>
                              {insight.category}
                            </span>
                          </div>
                          <h4 className="font-semibold text-gray-900">{insight.title}</h4>
                          {!isExpanded && (
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                              {insight.content}
                            </p>
                          )}
                        </div>
                        <ArrowRight className={`h-5 w-5 text-gray-400 flex-shrink-0 transition-transform ${
                          isExpanded ? 'rotate-90' : ''
                        }`} />
                      </button>

                      {isExpanded && (
                        <div className="px-4 pb-4 pt-0 ml-14">
                          <p className="text-sm text-gray-700 mb-4">
                            {insight.content}
                          </p>
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <span className="text-xs font-semibold text-green-800 uppercase">Recommended Action</span>
                                <p className="text-sm text-green-700 mt-0.5">{insight.actionable}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Upgrade CTA */}
              <div className="mt-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-100">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h4 className="font-semibold text-gray-900">Upgrade to Pro for Full Access</h4>
                    <p className="text-sm text-gray-600 mt-0.5">
                      Get personalized insights based on your actual brand data
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowModal(false)
                      navigate('/settings')
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors whitespace-nowrap flex items-center gap-2"
                  >
                    Upgrade
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-end">
          <button
            onClick={() => setShowModal(false)}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
